import React from 'react';

export interface GaugeDisplayProps {
  value: number | null;
  min?: number | null | undefined;
  max?: number | null | undefined;
  unit?: string | undefined;
}

// ─── Geometry ─────────────────────────────────────────────────────────────────
// Arc: 240° span, from SVG 150° (8 o'clock) to SVG 30° (4 o'clock), clockwise
// through the top (SVG 270° = 12 o'clock).  All angles are SVG convention:
// 0° = 3 o'clock, increasing clockwise, y-axis pointing down.

const CX = 100;
const CY = 92;
const R = 66;
const SW = 17;
const START_DEG = 150; // SVG angle of the left (low) endpoint
const SPAN_DEG = 240; // total arc sweep in degrees

// ─── Zone definitions (proportions of the full arc) ───────────────────────────
// Orange 32% | Green 36% | Red 32%

const ZONES = [
  { t1: 0, t2: 0.32, color: '#FF9800' },
  { t1: 0.32, t2: 0.68, color: '#00AB76' },
  { t1: 0.68, t2: 1.0, color: '#E11B1B' },
] as const;

function toXY(svgDeg: number, r: number): [number, number] {
  const rad = (svgDeg * Math.PI) / 180;
  return [CX + r * Math.cos(rad), CY + r * Math.sin(rad)];
}

// Draw a clockwise arc between two arc-fraction positions [0, 1].
function zonePath(t1: number, t2: number): string {
  const a1 = START_DEG + t1 * SPAN_DEG;
  const a2 = START_DEG + t2 * SPAN_DEG;
  const [x1, y1] = toXY(a1, R);
  const [x2, y2] = toXY(a2, R);
  const spanDeg = (t2 - t1) * SPAN_DEG;
  const largeArc = spanDeg > 180 ? 1 : 0;
  return `M ${x1.toFixed(2)} ${y1.toFixed(2)} A ${R} ${R} 0 ${largeArc} 1 ${x2.toFixed(2)} ${y2.toFixed(2)}`;
}

// ─── Value → gauge fraction [0, 1] ───────────────────────────────────────────
// [min, max] maps linearly across the full arc. Values outside are clamped.

function getGaugePct(
  value: number,
  min: number | null | undefined,
  max: number | null | undefined
): number {
  if (min == null || max == null || min >= max) {
    if (max != null && max > 0) {
      return Math.min(Math.max(value / max, 0), 1);
    }
    return 0;
  }
  return Math.min(Math.max((value - min) / (max - min), 0), 1);
}

// ─── Status from gauge fraction ───────────────────────────────────────────────

function getZoneStatus(pct: number): { label: string; color: string } {
  if (pct < 0.32) {
    return { label: 'Low', color: '#FF9800' };
  }
  if (pct <= 0.68) {
    return { label: 'Normal', color: '#00AB76' };
  }
  return { label: 'High', color: '#E11B1B' };
}

// ─── Component ────────────────────────────────────────────────────────────────

const GaugeDisplay: React.FC<GaugeDisplayProps> = ({
  value,
  min,
  max,
  unit,
}) => {
  const hasValue = value !== null;
  const pct = hasValue ? getGaugePct(value, min, max) : 0;
  const status = hasValue ? getZoneStatus(pct) : null;

  const needleAngleDeg = START_DEG + pct * SPAN_DEG;
  const needleLen = R - SW / 2 - 4;
  const [nx, ny] = toXY(needleAngleDeg, needleLen);

  const displayValue = value ?? 0;

  return (
    <svg
      width={200}
      height={152}
      viewBox="0 0 200 152"
      aria-label={`Gauge: ${displayValue}${unit ? ` ${unit}` : ''}${status ? `, ${status.label}` : ''}`}
    >
      {/* Background track */}
      <path
        d={zonePath(0, 1)}
        fill="none"
        stroke="#E5E7E9"
        strokeWidth={SW}
        strokeLinecap="butt"
      />

      {/* Colour zones */}
      {ZONES.map((zone, i) => (
        <path
          key={i}
          d={zonePath(zone.t1, zone.t2)}
          fill="none"
          stroke={zone.color}
          strokeWidth={SW}
          strokeLinecap="butt"
        />
      ))}

      {/* Rounded end caps — circles placed at the two outer arc tips */}
      {(() => {
        const [sx, sy] = toXY(START_DEG, R);
        const [ex, ey] = toXY(START_DEG + SPAN_DEG, R);
        const startColor = ZONES[0].color;
        const endColor = ZONES[ZONES.length - 1]?.color ?? ZONES[0].color;
        return (
          <>
            <circle
              cx={sx.toFixed(2)}
              cy={sy.toFixed(2)}
              r={SW / 2}
              fill={startColor}
            />
            <circle
              cx={ex.toFixed(2)}
              cy={ey.toFixed(2)}
              r={SW / 2}
              fill={endColor}
            />
          </>
        );
      })()}

      {/* Needle */}
      <line
        x1={CX}
        y1={CY}
        x2={nx.toFixed(2)}
        y2={ny.toFixed(2)}
        stroke="#181A1B"
        strokeWidth={2.5}
        strokeLinecap="round"
      />

      {/* Centre cap */}
      <circle cx={CX} cy={CY} r={5} fill="#181A1B" />

      {/* Numeric value */}
      <text
        x={CX}
        y={CY + 25}
        textAnchor="middle"
        fontSize={20}
        fontWeight={500}
        fill="#181A1B"
        fontFamily="Roboto, sans-serif"
      >
        {displayValue}
      </text>

      {/* Status label */}
      {status && (
        <text
          x={CX}
          y={CY + 40}
          textAnchor="middle"
          fontSize={11}
          fill={status.color}
          fontFamily="Roboto, sans-serif"
        >
          {status.label}
        </text>
      )}
    </svg>
  );
};

export default GaugeDisplay;
